import { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineBuildingOffice2, HiOutlineCheck, HiOutlineXMark, HiOutlineArrowPath } from "react-icons/hi2";
import toast from "react-hot-toast";
import { retreatBlocksAPI } from "../services/api";
import "./PropertyRooms.css";

const ymd = (d) => (d ? String(d).slice(0, 10) : "");
const fmtDay = (d) => (d ? new Date(`${ymd(d)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const plusDays = (d, n) => { const t = new Date(`${d}T12:00:00`); t.setDate(t.getDate() + n); return t.toISOString().slice(0, 10); };
const newRequestId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const STATUS = {
  reserved: { label: "Reserved", cls: "pr__pill--ok" },
  held:     { label: "Held",     cls: "pr__pill--hold" },
  released: { label: "Released", cls: "pr__pill--off" },
  lapsed:   { label: "Hold lapsed", cls: "pr__pill--off" },
};

/**
 * Rooms for one Forge retreat block, taken from the property's one room
 * and bed inventory (kept by Makua's system). What's reserved here
 * disappears from Makua's booking site for those nights, and the other
 * way round, so the two can never sell the same bed.
 */
export default function PropertyRooms({ block, canEdit }) {
  const [range, setRange] = useState({ start: ymd(block.start_date), end: ymd(block.end_date) });
  const [party, setParty] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ categoryId: "", status: "reserved", holdUntil: plusDays(new Date().toISOString().slice(0, 10), 7), label: "" });
  const [requestId, setRequestId] = useState(newRequestId);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true); setError("");
    retreatBlocksAPI.property.overview(block.id, { ...range, party })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [block.id, range, party]);
  useEffect(() => { load(); }, [load]);

  const cats = useMemo(() => data?.categories || [], [data]);
  const chosen = cats.find((c) => String(c.id) === String(form.categoryId));
  const holds = data?.holds || [];
  const live = holds.filter((h) => h.status === "reserved" || h.status === "held");
  const past = holds.filter((h) => !live.includes(h));
  const unitsHeld = live.reduce((n, h) => n + h.units.filter((u) => u.live).length, 0);

  const reserve = async (e) => {
    e.preventDefault();
    if (!chosen) return;
    setBusy("reserve");
    try {
      const r = await retreatBlocksAPI.property.reserve(block.id, {
        requestId, categoryId: chosen.id, party, start: range.start, end: range.end,
        status: form.status, holdUntil: form.status === "held" ? form.holdUntil : undefined, label: form.label.trim() || undefined,
      });
      const h = r.data.hold;
      toast.success(`${h.status === "held" ? "Held" : "Reserved"}: ${h.units.map((u) => (u.bed ? `${u.room} ${u.bed}` : u.room)).join(", ")}`);
      setRequestId(newRequestId());
      setForm((f) => ({ ...f, label: "" }));
      load();
    } catch (err) {
      toast.error(err.message);
      // A different attempt next time: this one may have failed for good.
      setRequestId(newRequestId());
      load();
    } finally { setBusy(""); }
  };

  const act = async (h, kind) => {
    if (kind === "release" && !window.confirm("Release these rooms to the property? Makua guests will be able to book them for those nights.")) return;
    setBusy(`${kind}:${h.ref}`);
    try {
      if (kind === "confirm") await retreatBlocksAPI.property.confirm(block.id, h.ref);
      else await retreatBlocksAPI.property.release(block.id, h.ref);
      toast.success(kind === "confirm" ? "Reserved." : "Released to the property.");
      load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(""); }
  };

  if (data && data.connected === false) {
    return (
      <section className="acard pr">
        <Head />
        <p className="pr__note">
          Not connected. To reserve rooms from the property's shared inventory, set <code>MAKUA_PROPERTY_URL</code> and{" "}
          <code>PROPERTY_API_KEY</code> on this backend, and the same <code>PROPERTY_API_KEY</code> on Makua's.
        </p>
      </section>
    );
  }

  return (
    <section className="acard pr" aria-busy={loading}>
      <Head count={unitsHeld} />

      <div className="pr__dates">
        <label className="pr__field">
          <span>Arrive</span>
          <input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
        </label>
        <label className="pr__field">
          <span>Leave</span>
          <input type="date" value={range.end} min={range.start} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
        </label>
        <label className="pr__field pr__field--narrow">
          <span>People</span>
          <input type="number" min={1} max={200} value={party} onChange={(e) => setParty(Math.max(1, Math.min(200, Number(e.target.value) || 1)))} />
        </label>
        <button type="button" className="a-btn a-btn--ghost pr__refresh" onClick={load} disabled={loading} aria-label="Refresh availability">
          <HiOutlineArrowPath className={loading ? "pr__spin" : ""} /> Refresh
        </button>
      </div>

      {error && (
        <p className="pr__error" role="alert">{error}</p>
      )}

      {!error && (
        <div className="pr__layout">
          <div className="pr__avail">
            <table className="pr__table">
              <caption className="pr__caption">Free {fmtDay(range.start)} – {fmtDay(range.end)}, for {party} {party === 1 ? "person" : "people"}</caption>
              <thead><tr><th>Room type</th><th>Sold by</th><th className="pr__num">Free</th></tr></thead>
              <tbody>
                {cats.map((c) => (
                  <tr key={c.id} className={c.fits ? "" : "pr__row--short"}>
                    <td>
                      <span className="pr__cat">{c.name}</span>
                      {c.group && <span className="pr__group">{c.group}</span>}
                    </td>
                    <td>{c.sellBy === "bed" ? "Bed" : `Room, ${c.guestsPerUnit} ${c.guestsPerUnit === 1 ? "guest" : "guests"}`}</td>
                    <td className="pr__num">
                      <b>{c.freeCount}</b> of {c.unitCount}
                      {!c.fits && c.freeCount > 0 && <span className="pr__short">needs {c.unitsNeeded}</span>}
                    </td>
                  </tr>
                ))}
                {!cats.length && !loading && <tr><td colSpan={3} className="pr__empty">No rooms in the inventory yet.</td></tr>}
              </tbody>
            </table>
          </div>

          {canEdit && (
            <form className="pr__form" onSubmit={reserve}>
              <p className="pr__form-title">Reserve for this retreat</p>
              <label className="pr__field">
                <span>Room type</span>
                <select value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))} required>
                  <option value="">Choose…</option>
                  {cats.map((c) => (
                    <option key={c.id} value={c.id} disabled={!c.fits}>
                      {c.name} ({c.freeCount} free{c.fits ? "" : ", not enough"})
                    </option>
                  ))}
                </select>
              </label>
              {chosen && (
                <p className="pr__hint">
                  {party} {party === 1 ? "person" : "people"} takes {chosen.unitsNeeded} {chosen.sellBy === "bed" ? (chosen.unitsNeeded === 1 ? "bed" : "beds") : (chosen.unitsNeeded === 1 ? "room" : "rooms")}. The property picks which.
                </p>
              )}
              <fieldset className="pr__choice">
                <legend>How</legend>
                <label><input type="radio" name="pr-status" checked={form.status === "reserved"} onChange={() => setForm((f) => ({ ...f, status: "reserved" }))} /> Reserve</label>
                <label><input type="radio" name="pr-status" checked={form.status === "held"} onChange={() => setForm((f) => ({ ...f, status: "held" }))} /> Hold until</label>
                {form.status === "held" && (
                  <input type="date" aria-label="Hold until" value={form.holdUntil} min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setForm((f) => ({ ...f, holdUntil: e.target.value }))} required />
                )}
              </fieldset>
              <label className="pr__field">
                <span>Note <em>(optional)</em></span>
                <input type="text" maxLength={80} placeholder="e.g. facilitators" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
              </label>
              <button type="submit" className="a-btn a-btn--rust" disabled={!chosen || !chosen.fits || busy === "reserve"}>
                {busy === "reserve" ? "Reserving…" : form.status === "held" ? "Hold rooms" : "Reserve rooms"}
              </button>
            </form>
          )}
        </div>
      )}

      {live.length > 0 && (
        <div className="pr__holds">
          <p className="pr__form-title">This retreat has</p>
          <ul className="pr__list">
            {live.map((h) => <HoldRow key={h.ref} h={h} canEdit={canEdit} busy={busy} onAct={act} />)}
          </ul>
        </div>
      )}
      {past.length > 0 && (
        <details className="pr__past">
          <summary>Released and lapsed ({past.length})</summary>
          <ul className="pr__list">
            {past.map((h) => <HoldRow key={h.ref} h={h} />)}
          </ul>
        </details>
      )}
    </section>
  );
}

const Head = ({ count }) => (
  <div className="pr__head">
    <HiOutlineBuildingOffice2 className="pr__icon" aria-hidden="true" />
    <div>
      <h3 className="pr__title">Rooms at the property</h3>
      <p className="pr__sub">
        From the shared room inventory: whatever is reserved here can't be booked by Makua guests for those nights.
        {count > 0 && <> <b>{count} {count === 1 ? "unit" : "units"}</b> held for this retreat.</>}
      </p>
    </div>
  </div>
);

const HoldRow = ({ h, canEdit, busy, onAct }) => {
  const st = STATUS[h.status] || STATUS.lapsed;
  const units = h.units.filter((u) => u.live || h.status === "released" || h.status === "lapsed");
  return (
    <li className="pr__hold">
      <div className="pr__hold-main">
        <span className={`pr__pill ${st.cls}`}>
          {st.label}{h.status === "held" && h.holdUntil ? ` to ${fmtDay(h.holdUntil)}` : ""}
        </span>
        <span className="pr__hold-units">
          {units.map((u) => (u.bed ? `${u.room} · ${u.bed}` : u.room)).join(", ")}
        </span>
        <span className="pr__hold-meta">
          {units[0]?.category} · {fmtDay(h.start)} – {fmtDay(h.end)}
          {h.label && /\(([^)]+)\)$/.test(h.label) ? ` · ${h.label.match(/\(([^)]+)\)$/)[1]}` : ""}
        </span>
      </div>
      {canEdit && (h.status === "held" || h.status === "reserved") && (
        <div className="pr__hold-actions">
          {h.status === "held" && (
            <button type="button" className="a-btn a-btn--outline" disabled={!!busy} onClick={() => onAct(h, "confirm")}>
              <HiOutlineCheck /> Reserve
            </button>
          )}
          <button type="button" className="a-btn a-btn--ghost" disabled={!!busy} onClick={() => onAct(h, "release")}>
            <HiOutlineXMark /> Release
          </button>
        </div>
      )}
    </li>
  );
};
